// pages/FormViewerPage/hooks/useFormViewer/effectsForm.ts — Form loading effect
import { useEffect } from 'react';
import { supabase } from '../../../../supabase/client';
import type { Form, ShippingSettings, PaymentSettings, VariantDisplayStyle } from '../../../../types';
import { setPageMetaWithImage } from '../../../../utils/meta';
import { logError } from '../../../../utils/errorLogger';
import { trackFormView } from '../../../../utils/trackingUtils';
import { onIdle } from '../../../../utils/idleCallback';
import { fetchGatewayStatus } from '../../../../services/paymentGatewayStatus';
import { loadFormData, fetchProductData, loadBrandWarehouse, preloadMainImage, resolveFormHeroImage, computeSortedPaymentKeys } from '../../services/formViewerService';
import type { FormViewerState } from './state';

export function useFormViewerFormEffects(s: FormViewerState) {
    const { identifier, setNotFound, setLoading, setFormInactive, setForm, setLinkedProductName, setProductOptionsOverride, setSelectedShippingKey, setSelectedPaymentKey, setPixelsLoaded, navigate } = s;

    // Form loading
    useEffect(() => {
        const applyLoadedForm = (normalizedForm: Form) => {
            if (normalizedForm.productId) {
                fetchProductData(normalizedForm.productId).then(pd => {
                    if (pd?.name) setLinkedProductName(pd.name);
                    if (pd?.attributes?.variantOptions) {
                        const vo = pd.attributes.variantOptions as Array<{ name: string; values: string[] }>;
                        if (Array.isArray(vo) && vo.length > 0) setProductOptionsOverride(vo.map((opt, idx) => ({ id: idx + 1, name: opt.name || `Opsi ${idx + 1}`, values: Array.isArray(opt.values) ? opt.values : [], displayStyle: 'radio' as VariantDisplayStyle, showPrice: true })));
                    }
                }).catch(e => logError(e instanceof Error ? e : String(e), 'form_load_product', { productId: normalizedForm.productId }));
            }
            const cleanForm = { ...normalizedForm, productImages: [] };
            setForm(cleanForm);
            setPageMetaWithImage(normalizedForm.title, `Pesan ${normalizedForm.title} sekarang - Proses cepat, pengiriman ke seluruh Indonesia.`, normalizedForm.mainImage || undefined);

            if (normalizedForm.brandId) {
                loadBrandWarehouse(normalizedForm.brandId, normalizedForm.everproSettings).then(whData => {
                    if (whData) setForm(prev => prev ? { ...prev, everproSettings: { ...prev.everproSettings!, ...whData } } : prev);
                }).catch(e => logError(e instanceof Error ? e : String(e), 'form_load_warehouse', { brandId: normalizedForm.brandId }));
            }

            // Preload hero = FOTO VARIAN bila URL preselect varian (speed
            // 2026-10-22, mirror head preload + backend). Tanpa ini React
            // preload mainImage sementara render foto varian → mubazir.
            const heroUrl = resolveFormHeroImage(normalizedForm) || normalizedForm.mainImage;
            if (heroUrl) preloadMainImage(heroUrl);
            // Analytics page-view susul saat idle — jangan berebut koneksi dgn paint.
            const fv = () => trackFormView(normalizedForm.id, normalizedForm.title, normalizedForm.productId, normalizedForm.brandId ?? undefined);
            if ('requestIdleCallback' in window) (window as any).requestIdleCallback(fv, { timeout: 5000 });
            else setTimeout(fv, 1500);

            // Pre-warm status gateway saat isi step 1 (idle) — step 3 langsung
            // tampil tanpa nunggu RPC (memo 60 dtk, tanpa fetch ganda).
            if (normalizedForm.brandId) {
                onIdle(() => { fetchGatewayStatus(normalizedForm.brandId).catch(() => {}); }, 4000);
            }

            // Kunci flat legacy ('flat'/'flat_*') yang masih tersimpan di DB disembunyikan —
            // flat DIHAPUS 2026-10-10, semua ongkir = tarif real agregator.
            const settingsRecord = normalizedForm.shippingSettings as unknown as Record<string, { visible?: boolean }>;
            const visibleShipping = (Object.keys(settingsRecord)).filter(key => key !== 'flat' && !key.startsWith('flat_') && settingsRecord[key]?.visible) as Array<keyof ShippingSettings>;
            if (normalizedForm.addressInputMode === 'simple') {
                // Mode Simpel (lead only): tanpa ongkir & pembayaran — kosongkan pilihan.
                setSelectedShippingKey(undefined);
                setSelectedPaymentKey(undefined);
            } else if (normalizedForm.addressInputMode === 'flat_regular') {
                // Flat DIHAPUS 2026-10-10: default = metode non-gratis pertama (biasanya 'regular'
                // = Bayar via Everpro, ongkir 0 sampai kurir real dipilih / dihitung sistem).
                const firstNonFree = visibleShipping.find(k => k !== 'free');
                setSelectedShippingKey(firstNonFree || 'regular');
            } else {
                setSelectedShippingKey(visibleShipping[0]);
            }
            // Saat form baru dimuat kurir belum dipilih (ongkir 0) → filter ongkir
            // belum aktif; sinkronisasi ulang ditangani effectsSync begitu tarif dipilih.
            // Form Sederhana ('none' non-Jawa) dan Wizard step 1 = tanpa pembayaran
            // (ongkir belum diketahui) → JANGAN auto-pilih, biar tak ada baris
            // "Biaya COD" menggantung & anti TF buta. Wizard pilih bayar di step 3.
            const isFormSederhana = (normalizedForm.addressInputMode === 'none' || normalizedForm.addressInputMode === 'wizard') && !normalizedForm.javaOnly;
            if (isFormSederhana) {
                setSelectedPaymentKey(undefined);
            } else {
                const sortedVisiblePayment = computeSortedPaymentKeys(normalizedForm, s.addressData, 0);
                if (sortedVisiblePayment.length === 0) {
                    setSelectedPaymentKey('bankTransfer');
                } else if (!sortedVisiblePayment.includes(s.selectedPaymentKey as keyof PaymentSettings)) {
                    setSelectedPaymentKey(sortedVisiblePayment[0]);
                }
            }
            setPixelsLoaded(true);
            setLoading(false);
        };
        const fetchForm = async () => {
            if (!identifier) { setNotFound(true); setLoading(false); return; }
            // Hydrasi statis: baked __FORM_DATA__ cocok → pakai langsung, tanpa fetch.
            // Guard slug per render (navigasi /f/a → /f/b tanpa reload tetap fetch ulang).
            const baked = (s as any).bakedForm as Record<string, unknown> | null | undefined;
            if (baked && String((baked as any).slug || '').toLowerCase() === String(identifier).toLowerCase()) {
                // Baked sudah jadi state awal (loading=false) — JANGAN setLoading(true)
                // di sini: itu memaksa 1 frame FormSkeleton padahal konten siap
                // (kedip tahap-3, 2026-10-26). Langsung apply, selesai.
                setNotFound(false); setFormInactive(false);
                applyLoadedForm(baked as unknown as Form);
                return;
            }
            setLoading(true); setNotFound(false); setFormInactive(false);
            const result = await loadFormData(identifier);
            if (result.notFound) { setNotFound(true); setLoading(false); return; }
            if (result.formInactive) { setFormInactive(true); setLoading(false); return; }
            // Slug lama -> redirect permanen ke URL baru (ganti slug pasca-publish).
            if ((result as { redirectedTo?: string }).redirectedTo) {
                navigate(`/f/${(result as { redirectedTo?: string }).redirectedTo}${window.location.search}`, { replace: true });
                setLoading(false);
                return;
            }

            const normalizedForm = result.foundForm!;
            applyLoadedForm(normalizedForm);
        };
        fetchForm().catch(e => { logError(e instanceof Error ? e : String(e), 'form_load', { identifier }); setLoading(false); });
    }, [identifier]);
}
